import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@truefit/http-utils';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { ClipGroupModel, CreateClipGroupInput } from '../types';

interface CreateClipGroupParams {
  // playbyplayId is a string post-migration: SES Event.id is `string` at the
  // proto level, and shifts use composite ids like "shift_14037_1" that aren't
  // integer-coercible. Numeric SES events ("1161") and custom event PKs flow
  // through here as their numeric-string form.
  playbyplayId: string;
  data: CreateClipGroupInput;
}

const useCreateClipGroupMutation = () => {
  const queryClient = useQueryClient();
  const gameId = useCurrentGameId();

  return useMutation({
    mutationFn: async ({ playbyplayId, data }: CreateClipGroupParams) => {
      const response = await post<{ success: boolean; clipGroup: ClipGroupModel }>(
        `/v1.0/games/${gameId}/play-by-play/${playbyplayId}/clip-groups`,
        data
      );
      return response.data.clipGroup;
    },
    onSuccess: (_, variables) => {
      // Invalidate the clip groups query for this play-by-play event
      queryClient.invalidateQueries({
        queryKey: ['Games', gameId, 'PlayByPlay', variables.playbyplayId, 'ClipGroups']
      });
      // Also invalidate game-level clip groups
      queryClient.invalidateQueries({
        queryKey: ['Games', gameId, 'ClipGroups']
      });
      // Invalidate clip group counts to update event action icons
      queryClient.invalidateQueries({
        queryKey: ['Games', gameId, 'ClipGroupCounts']
      });
    },
  });
};

export default useCreateClipGroupMutation;
